import { clamp } from './statistics'
import { decimalToImplied } from './odds'

export interface EvBreakdown {
  ev: number
  evPerUnit: number
  evPct: number
  edge: number
  breakEvenProb: number
  modelProb: number
  marketProb: number
  payoutIfWin: number
  lossIfLose: number
  decision: EvDecision
  rationale: string
}

export type EvDecision = 'strong' | 'small' | 'paper' | 'skip'

export interface EvInputs {
  modelProb: number
  decimalOdds: number
  stake?: number
  feePct?: number
  spreadPct?: number
  slippagePct?: number
  minEdge?: number
}

export function expectedValue(inputs: EvInputs): EvBreakdown {
  const stake = inputs.stake ?? 1
  const feePct = inputs.feePct ?? 0
  const spreadPct = inputs.spreadPct ?? 0
  const slippagePct = inputs.slippagePct ?? 0
  const minEdge = inputs.minEdge ?? 0.02
  const p = clamp(inputs.modelProb, 0, 1)

  const frictionMultiplier = 1 - clamp(spreadPct + slippagePct, 0, 0.99)
  const effectiveDecimal = Math.max(1, inputs.decimalOdds * frictionMultiplier)

  const grossProfit = (effectiveDecimal - 1) * stake
  const fee = grossProfit > 0 ? grossProfit * feePct : 0
  const payoutIfWin = grossProfit - fee
  const lossIfLose = stake

  const ev = p * payoutIfWin - (1 - p) * lossIfLose
  const evPerUnit = stake > 0 ? ev / stake : 0
  const marketProb = decimalToImplied(inputs.decimalOdds)
  const breakEvenProb = breakEvenProbability(effectiveDecimal, feePct)
  const edge = p - marketProb

  const decision = decideAction(edge, evPerUnit, minEdge)

  return {
    ev,
    evPerUnit,
    evPct: evPerUnit * 100,
    edge,
    breakEvenProb,
    modelProb: p,
    marketProb,
    payoutIfWin,
    lossIfLose,
    decision,
    rationale: buildRationale(edge, evPerUnit, breakEvenProb, p, minEdge),
  }
}

export function breakEvenProbability(decimalOdds: number, feePct = 0): number {
  const b = (decimalOdds - 1) * (1 - feePct)
  if (b <= 0) return 1
  return clamp(1 / (1 + b), 0, 1)
}

export function breakEvenDecimalOdds(prob: number): number {
  const p = clamp(prob, 1e-9, 1)
  return 1 / p
}

export function riskReward(entry: number, stop: number, target: number): {
  risk: number
  reward: number
  ratio: number
  valid: boolean
} {
  const risk = Math.abs(entry - stop)
  const reward = Math.abs(target - entry)
  const valid = risk > 0 && reward > 0
  return { risk, reward, ratio: risk > 0 ? reward / risk : 0, valid }
}

export function decideAction(edge: number, evPerUnit: number, minEdge = 0.02): EvDecision {
  if (edge <= 0 || evPerUnit <= 0) return 'skip'
  if (edge < minEdge) return 'paper'
  if (edge < minEdge * 2.5) return 'small'
  return 'strong'
}

function buildRationale(
  edge: number,
  evPerUnit: number,
  breakEven: number,
  modelProb: number,
  minEdge: number,
): string {
  if (edge <= 0 || evPerUnit <= 0) {
    return `No edge: model probability ${(modelProb * 100).toFixed(1)}% is below the ${(breakEven * 100).toFixed(1)}% break-even. Negative expected value — skip.`
  }
  if (edge < minEdge) {
    return `Thin edge (${(edge * 100).toFixed(1)}%) under the ${(minEdge * 100).toFixed(1)}% threshold. Paper-trade only until confirmed.`
  }
  return `Positive expected value with ${(edge * 100).toFixed(1)}% edge above the ${(breakEven * 100).toFixed(1)}% break-even. Size conservatively.`
}

export function expectedValueFromProbs(modelProb: number, marketProb: number, stake = 1): number {
  const decimal = marketProb > 0 ? 1 / marketProb : 1
  const p = clamp(modelProb, 0, 1)
  return p * (decimal - 1) * stake - (1 - p) * stake
}
